import React, { useEffect, useState } from 'react';
import {
  isPushSupported,
  isStandalonePwa,
  subscribeToPushNotifications,
  syncExistingPushSubscription,
  unsubscribeFromPushNotifications,
} from '../lib/push-notifications';

const PushNotificationCard: React.FC = () => {
  const [status, setStatus] = useState<'idle' | 'enabled' | 'unsupported' | 'loading' | 'error'>(
    'idle',
  );
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      setMessage('Este navegador no soporta notificaciones push.');
      return;
    }

    if (Notification.permission === 'granted') {
      syncExistingPushSubscription()
        .then((hasSubscription) => {
          if (hasSubscription) {
            setStatus('enabled');
            setMessage('Las notificaciones están activas en este dispositivo.');
          } else {
            setStatus('idle');
            setMessage('');
          }
        })
        .catch(() => {
          setStatus('idle');
        });
    } else if (Notification.permission === 'denied') {
      setStatus('error');
      setMessage('Las notificaciones están bloqueadas en el navegador.');
    }
  }, []);

  const enableNotifications = async () => {
    setStatus('loading');
    setMessage('Activando notificaciones...');
    try {
      await subscribeToPushNotifications();
      setStatus('enabled');
      setMessage('Listo. Te avisaremos sobre actualizaciones importantes.');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'No se pudieron activar notificaciones.');
    }
  };

  const disableNotifications = async () => {
    setStatus('loading');
    setMessage('Desactivando notificaciones...');
    try {
      await unsubscribeFromPushNotifications();
      setStatus('idle');
      setMessage('Las notificaciones se desactivaron en este dispositivo.');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'No se pudieron desactivar notificaciones.');
    }
  };

  const shouldExplainIosInstall = /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !isStandalonePwa();
  const canEnable = status !== 'loading' && status !== 'enabled' && status !== 'unsupported';

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-primary mb-3">Notificaciones</h2>
      <p className="text-gray-600 mb-4">
        Recibe avisos importantes de tu asistente aunque no tengas la app abierta.
      </p>
      {shouldExplainIosInstall && (
        <p className="text-sm text-[#CC9944] bg-[#FFF8E5] border-l-4 border-[#FFE5B2] p-3 mb-4">
          En iPhone o iPad, instala primero la app desde Safari con Compartir y Agregar a pantalla
          de inicio. Después abre la app instalada para activar notificaciones.
        </p>
      )}
      {message && <p className="text-sm text-gray-600 mb-4">{message}</p>}
      <div className="space-y-3">
        <button
          type="button"
          onClick={enableNotifications}
          disabled={!canEnable}
          className={`w-full py-2 px-4 rounded-lg transition-colors text-center ${
            canEnable
              ? 'bg-secondary text-white hover:bg-secondary/90'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {status === 'loading'
            ? 'Activando...'
            : status === 'enabled'
              ? 'Notificaciones activas'
              : 'Activar notificaciones'}
        </button>
        {status === 'enabled' && (
          <button
            type="button"
            onClick={disableNotifications}
            className="w-full py-2 px-4 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-center"
          >
            Desactivar en este dispositivo
          </button>
        )}
      </div>
    </div>
  );
};

export default PushNotificationCard;
